import type { Child, Document, Manifest, ManifestIntent, ManifestReference, ManifestType, Reference } from '../api/authoring'
import { arranges } from './composition'
import { acceptsUpload, fieldsSchema, intentWrites, proseOf, typeOf } from './manifest'
import { fieldOf, parseBinding, type PatternDeclaration, type PatternNode } from './pattern'
import { orderedProperties, resolve, type Schema } from './schema'
import { declaredReferences } from './references'
import { canonicalHost, flattenRoutes, resolveRouting, websiteFields } from './website'

// Records as a site's owner meets them (finishing-patterns §11).
//
// An owner thinks "the albums", not "the Collection a record type names",
// and "an album and its photos", not "a record and the Gallery it owns".
// These helpers read the agency's declarations off the manifest so a screen
// can say the first and act on the second.

/** The record types whose new records join a Collection: what an owner edits as a list. */
export function listedTypes(manifest: Manifest | undefined): ManifestType[] {
  return (manifest?.types ?? []).filter((declared) => declared.role === 'record' && Boolean(declared.collection))
}

/** The relations a record owns, and so edits in place. */
export function ownedReferences(manifest: Manifest | undefined, typeName: string): ManifestReference[] {
  return declaredReferences(manifest, typeName).filter((reference) => reference.owned === true)
}

/**
 * The relations an owner may point at one of the record's own things: a
 * cover chosen from the album's photos. Single, not owned, and accepting a
 * type the owned targets hold.
 */
export function choosableReferences(manifest: Manifest | undefined, typeName: string): ManifestReference[] {
  return declaredReferences(manifest, typeName).filter(
    (reference) => reference.owned !== true && reference.multiple !== true && reference.authoredNames !== true,
  )
}

/**
 * The choosable relations an owner fills from the record's own things: an
 * album's cover, one of the photos its gallery arranges.
 *
 * Read off the declaration, not the document, and per relation. A
 * relation is chosen among owned things where something the record owns
 * arranges what the relation accepts, so an album that has lost its
 * gallery is missing something, not a type that picks from the library
 * instead. Owning *something* is not enough: a pest owns its words, and
 * the pictures bound inside them are part of the text, not a set to pick
 * its icon from.
 */
export function ownedChoices(manifest: Manifest | undefined, typeName: string): ManifestReference[] {
  const sources = ownedReferences(manifest, typeName)
    .flatMap((reference) => reference.accepts)
    .filter((accepted) => arranges(manifest, accepted))
    .flatMap((accepted) => typeOf(manifest, accepted)?.contains ?? [])
  return choosableReferences(manifest, typeName).filter((reference) =>
    reference.accepts.some((accepted) => sources.includes(accepted)),
  )
}

/**
 * The choosable relations an owner fills from the tenant's own things
 * rather than the record's: a service's picture, or a pest's icon, from
 * the library. Every choosable relation `ownedChoices` does not take.
 */
export function libraryChoices(manifest: Manifest | undefined, typeName: string): ManifestReference[] {
  const owned = new Set(ownedChoices(manifest, typeName).map((reference) => reference.name))
  return choosableReferences(manifest, typeName).filter((reference) => !owned.has(reference.name))
}

/**
 * The one type a relation accepts whose components hold a file, if that
 * is all it accepts: what the library picker is opened on, which can
 * take a new upload as well as an existing one. Any other relation is
 * chosen with the general picker.
 */
export function fileTarget(manifest: Manifest | undefined, declares: ManifestReference): string | undefined {
  const [only, ...rest] = declares.accepts
  return only && rest.length === 0 && acceptsUpload(manifest, only) ? only : undefined
}

/** The one reference a record holds under a name, if any. */
export function heldUnder(references: Reference[] | undefined, name: string): Reference | undefined {
  return (references ?? []).find((reference) => reference.name === name)
}

/** The members of a Collection, in its order, as linked children. */
export function members(collection: Document | undefined): Child[] {
  if (!collection) return []
  const items = (collection.fields as { items?: string[] } | undefined)?.items ?? []
  const byLink = new Map(collection.children.map((child) => [child.linkId, child]))
  return items.flatMap((linkId) => {
    const child = byLink.get(linkId)
    return child?.id ? [child] : []
  })
}

/** Where a record of this type lives on this site, and how its address is spelt. */
export interface RecordAddress {
  /** The URL path with the key's place marked, as `/gallery/:album`. */
  pattern: string
  /** The path up to the key, as `/gallery`. */
  parent: string
  /** The canonical host, to link to the live page. */
  host?: string
  /** The pattern that lays out each record's page, by component id. */
  layout?: string
  /** The name the route reads each record under: `pest` in `$pest.navTitle`. */
  param?: string
  /** The route's menu name, as an expression over the record. */
  title?: string
  /** The route's menu description, as an expression over the record. */
  description?: string
}

/**
 * The generated route that serves a page for each member of this
 * Collection, if the site has one.
 *
 * Matched by the Website's own `routeSource` edge, which is where a
 * generated route names its source.
 */
export function recordAddress(website: Document | undefined, collection: string | undefined): RecordAddress | undefined {
  if (!website || !collection) return undefined
  const edges = new Map((website.references ?? []).map((reference) => [reference.edgeId, reference]))
  const route = flattenRoutes(resolveRouting(website)).find((candidate) => {
    const source = candidate.route.source ? edges.get(candidate.route.source) : undefined
    return candidate.generated && source?.name === 'routeSource' && source.id === collection
  })
  if (!route) return undefined
  const parent = route.urlPath.replace(/\/:[^/]+$/, '') || '/'
  const layout = route.route.pattern ? edges.get(route.route.pattern)?.id : undefined
  return {
    pattern: route.urlPath,
    parent,
    host: canonicalHost(websiteFields(website)),
    layout,
    param: route.route.param,
    title: route.route.title,
    description: route.route.description,
  }
}

/** A record's own path on this site, or nothing when it has no key yet. */
export function recordPath(address: RecordAddress | undefined, key: string | undefined): string | undefined {
  if (!address || !key) return undefined
  return `${address.parent === '/' ? '' : address.parent}/${key}`
}

/** The field a record type is keyed by, and the one it is named by. */
export function keyAndLabel(manifest: Manifest | undefined, typeName: string): { key?: string; label?: string } {
  const declared = typeOf(manifest, typeName)
  return { key: declared?.key, label: labelField(manifest, typeName) }
}

/**
 * The field a new record is named by: the first its type says names it.
 * A type may list more, tried in order, and the rest are fallbacks the
 * platform reads when the first is empty rather than more for an author
 * to fill in.
 */
function labelField(manifest: Manifest | undefined, typeName: string): string | undefined {
  return typeOf(manifest, typeName)?.label?.[0]
}

/**
 * A route segment from a title: lower case, words joined by hyphens, as
 * the key pattern requires. Empty when nothing in the title survives.
 */
export function slugify(title: string): string {
  const slug = title
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  // The pattern begins with a letter.
  return slug.replace(/^[0-9-]+/, '')
}

/**
 * What to call a Collection: its own name when an author gave it one (the
 * listing's label), else what it lists where a record type names it
 * ("Albums"), and its id otherwise.
 */
export function collectionTitle(manifest: Manifest | undefined, id: string, label?: string): string {
  if (label) return label
  const listing = listedTypes(manifest).find((declared) => declared.collection === id)
  return listing ? proseOf(listing, listing.name).plural : id
}

/**
 * The record type a Collection holds: the type whose records join it, or
 * else the type of what it already holds.
 */
export function recordTypeOf(
  manifest: Manifest | undefined,
  id: string,
  collection: Document | undefined,
): string | undefined {
  if (!id) return undefined
  const declared = (manifest?.types ?? []).find((entry) => entry.collection === id)
  if (declared) return declared.name
  return collection?.children.find((child) => typeOf(manifest, child.type)?.role === 'record')?.type
}

/** One field a set of records can be divided by, and the values it takes. */
export interface DividingField {
  name: string
  values: Array<string | boolean>
}

/**
 * What a set of records can be divided by: the booleans and the enums,
 * with their values. These are the only fields a membership filter may
 * compare, which is `component.Filter`'s rule and not this client's.
 */
export function dividingFields(fields: Schema | undefined): DividingField[] {
  if (!fields) return []
  return orderedProperties(fields, fields).flatMap((property): DividingField[] => {
    const schema = resolve(property.schema, fields)
    if (schema?.type === 'boolean') return [{ name: property.name, values: [true, false] }]
    if (schema?.enum) return [{ name: property.name, values: schema.enum as string[] }]
    return []
  })
}

/**
 * The member of a payload that is a membership filter, if it has one.
 *
 * Found by its shape — an object of exactly a `field` and an `equals` —
 * because `equals` is untyped on the wire: it is a boolean against one
 * field and a string against another, so a derived form has no box to
 * draw for it and would send a filter missing its required half.
 */
export function filterMember(payload: Schema | undefined): string | undefined {
  if (!payload) return undefined
  return orderedProperties(payload, payload).find((property) => {
    const members = Object.keys(resolve(property.schema, payload)?.properties ?? {}).sort()
    return members.length === 2 && members[0] === 'equals' && members[1] === 'field'
  })?.name
}

/**
 * Where a record's page carries a field, other than on the page itself.
 *
 * `address` is the record's key and whatever the route binds into its
 * menu entry, which the page editor puts under "Address and menus". Any
 * other value is a field of the page's root node, named as that node
 * names it: `seo` for `seo.description`, `title` for `title`.
 */
export type SettingsSection = string

/** The section that holds the key and the route's menu entry. */
export const ADDRESS_SECTION = 'address'

/**
 * Which record fields are the page's settings rather than what it says,
 * and under which section each one goes.
 *
 * **Read from how the site binds the record, never from field names.** A
 * field is a setting when it is the type's key, when the generated route
 * reads it for the menu, or when every binding of it in the layout
 * pattern lands on the root node, the page itself — and in the last two
 * cases, only when the page does not also show it. One binding into a
 * block under a slot means the page shows it, and it stays with the
 * content: `title` is a pest's page title and its heading, so it is
 * content. A field nothing on the page binds stays with the content too,
 * because nothing here says otherwise: a pest's `cardTitle` is shown on
 * its tile in the index, which is another page's content.
 *
 * A field bound into two root fields goes under the first one the
 * pattern binds it to.
 */
export function settingsFields(
  manifest: Manifest | undefined,
  typeName: string,
  address: RecordAddress | undefined,
  layout: PatternDeclaration | undefined,
): Map<string, SettingsSection> {
  const placed = new Map<string, SettingsSection>()
  if (!address) return placed

  const key = typeOf(manifest, typeName)?.key
  if (key) placed.set(key, ADDRESS_SECTION)

  // What the page shows, and what it carries only on itself, from the
  // pattern that lays it out.
  const shown = new Set<string>()
  const onPage = new Map<string, SettingsSection>()
  if (layout) {
    const scope = layout.input.name
    const read = (node: PatternNode, root: boolean) => {
      for (const [destination, expression] of Object.entries(node.bindings ?? {})) {
        const binding = parseBinding(expression)
        if (!binding || binding.scope !== scope) continue
        if (!root) shown.add(binding.field)
        else if (!onPage.has(binding.field)) onPage.set(binding.field, fieldOf(destination))
      }
      for (const child of node.children ?? []) read(child, false)
    }
    read(layout.tree, true)
  }

  // The route's menu entry, unless the page shows the same field: a
  // route that names each page by its title is not a reason to move the
  // title off the content.
  for (const expression of [address.title, address.description]) {
    const binding = expression ? parseBinding(expression) : undefined
    if (binding && binding.scope === address.param && !shown.has(binding.field) && !placed.has(binding.field)) {
      placed.set(binding.field, ADDRESS_SECTION)
    }
  }

  for (const [field, section] of onPage) {
    if (!shown.has(field) && !placed.has(field)) placed.set(field, section)
  }
  return placed
}

/**
 * The settings section an intent belongs in, or nothing where it stays
 * with the content.
 *
 * An intent moves only when every field it writes is a setting, because
 * one intent is one payload and a form cannot be split across two tabs.
 * Its fields are its payload's members, which a tenant type's intent
 * names exactly.
 */
export function intentSection(intent: ManifestIntent, placed: Map<string, SettingsSection>): SettingsSection | undefined {
  const writes = intentWrites(intent)
  if (writes.length === 0 || !writes.every((field) => placed.has(field))) return undefined
  return placed.get(writes[0])
}

/**
 * The settings sections in the page editor's order: the root node's
 * fields as its own schema orders them, then the address.
 */
export function sectionOrder(manifest: Manifest | undefined, layout: PatternDeclaration | undefined): SettingsSection[] {
  const schema = layout ? fieldsSchema(manifest, layout.tree.type) : undefined
  const fields = schema ? orderedProperties(schema, schema).map((property) => property.name) : []
  return [...fields, ADDRESS_SECTION]
}
